import { createRoute, Link, useNavigate } from "@tanstack/react-router";
import { withPermission } from "../lib/with-permission";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { rootRoute } from "./__root";
import { PageHeader } from "../components/ui/PageHeader/PageHeader";
import { Card } from "../components/ui/Card/Card";
import { Table } from "../components/ui/Table/Table";
import { Modal } from "../components/ui/Modal/Modal";
import { LoadingSpinner } from "../components/ui/LoadingSpinner/LoadingSpinner";
import { ErrorFallback } from "../components/ui/ErrorFallback/ErrorFallback";
import { Pagination } from "../components/ui/Pagination/Pagination";
import { FilterBar } from "../components/ui/FilterBar/FilterBar";
import { StatusBadge } from "../components/ui/StatusBadge/StatusBadge";
import { formatQuantity, formatDate, formatAmount } from "../lib/date";
import {
	useStockItemList,
	useStockItemDetail,
	useStockItemMovements,
	useStockItemSerials,
} from "../hooks/use-stock-items";
import styles from "./StockItems.module.css";

export const route = createRoute({
	getParentRoute: () => rootRoute,
	path: "/stock-items",
	validateSearch: (search: Record<string, unknown>) => ({
		page: Number(search.page) || 1,
		size: Number(search.size) || 20,
		search: String(search.search || ""),
		createdAtFrom: String(search.createdAtFrom || ""),
		createdAtTo: String(search.createdAtTo || ""),
		status: String(search.status || ""),
	}),
	component: withPermission(StockItemsPage, "stock_items", "read"),
});

function StockItemsPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { page, size, search, createdAtFrom, createdAtTo, status } = route.useSearch();
	const pageSize = size;
	const [stockModalItem, setStockModalItem] = useState<{ id: string; name: string } | null>(null);
	const [movementsModalItem, setMovementsModalItem] = useState<{ id: string; name: string } | null>(
		null,
	);
	const [serialsModalItem, setSerialsModalItem] = useState<{ id: string; name: string } | null>(
		null,
	);
	const { data, isLoading, isError, refetch } = useStockItemList({
		search: search || undefined,
		createdAtFrom: createdAtFrom || undefined,
		createdAtTo: createdAtTo || undefined,
		status: status || undefined,
		page,
		limit: pageSize,
	});

	const items = data?.items ?? [];
	const total = data?.total ?? 0;

	return (
		<div>
			<PageHeader title={t("stockItems")} />

			<div className={styles.filters}>
				<FilterBar
					filters={[
						{ key: "search", type: "text", label: t("search") },
						{ key: "createdAt", type: "dateRange", label: t("createdAt") },
						{
							key: "status",
							type: "select",
							label: t("all"),
							options: [
								{ value: "active", label: t("active") },
								{ value: "inactive", label: t("inactive") },
							],
						},
					]}
					values={{ search, createdAtFrom, createdAtTo, status }}
					onChange={(vals) => {
						navigate({
							to: ".",
							search: {
								search: vals.search || "",
								createdAtFrom: vals.createdAtFrom || "",
								createdAtTo: vals.createdAtTo || "",
								status: vals.status || "",
								page: 1,
								size,
							},
						});
					}}
				/>
			</div>

			<Card>
				{isLoading && <LoadingSpinner />}
				{isError && <ErrorFallback onRetry={refetch} />}
				{!isLoading && !isError && (
					<Table
						columns={[
							{ key: "code", header: t("code") },
							{ key: "name", header: t("name") },
							{ key: "category", header: t("category") },
							{ key: "uom", header: t("uom") },
							{
								key: "isSalesItem",
								header: t("salesItem"),
								render: (row) => (row.isSalesItem ? t("yes") : t("no")),
							},
							{
								key: "isActive",
								header: t("status"),
								render: (row) => (
									<StatusBadge status={row.isActive ? "active" : "inactive"} type="stockItem" />
								),
							},
							{
								key: "stock",
								header: t("stock"),
								render: (row) => (
									<button
										type="button"
										className={styles.stockButton}
										onClick={() => setStockModalItem({ id: row.id, name: row.name })}
									>
										{formatQuantity(row.totalStock)}
									</button>
								),
							},
							{
								key: "stockRecordList",
								header: t("stockRecordList"),
								render: (row) => (
									<button
										type="button"
										className={styles.stockButton}
										onClick={() => setMovementsModalItem({ id: row.id, name: row.name })}
									>
										{t("list")}
									</button>
								),
							},
							{
								key: "serialList",
								header: t("serialList"),
								render: (row) =>
									row.manageSerialNumbers ? (
										<button
											type="button"
											className={styles.stockButton}
											onClick={() => setSerialsModalItem({ id: row.id, name: row.name })}
										>
											{t("showSerials")}
										</button>
									) : null,
							},
							{
								key: "actions",
								header: t("actions"),
								render: (row) => (
									<Link to={`/stock-items/${row.id}` as never} className={styles.link}>
										{t("viewDetails")}
									</Link>
								),
							},
						]}
						data={items}
						keyExtractor={(row) => row.id}
					/>
				)}
				<Pagination
					page={page}
					pageSize={pageSize}
					total={total}
					onPageChange={(p) => navigate({ to: ".", search: { page: p, size } })}
					onPageSizeChange={(s) => navigate({ to: ".", search: { size: s, page: 1 } })}
				/>
			</Card>

			{stockModalItem && (
				<StockModal item={stockModalItem} onClose={() => setStockModalItem(null)} />
			)}
			{movementsModalItem && (
				<MovementsModal item={movementsModalItem} onClose={() => setMovementsModalItem(null)} />
			)}
			{serialsModalItem && (
				<SerialsModal item={serialsModalItem} onClose={() => setSerialsModalItem(null)} />
			)}
		</div>
	);
}

function StockModal({
	item,
	onClose,
}: {
	item: { id: string; name: string };
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { data, isLoading } = useStockItemDetail(item.id);
	const warehouses = (data?.warehouseStocks ?? []).filter(
		(ws) => parseFloat(ws.currentStock ?? "0") > 0,
	);
	const totalStock = warehouses.reduce((sum, ws) => sum + parseFloat(ws.currentStock ?? "0"), 0);

	return (
		<Modal
			isOpen
			onClose={onClose}
			title={`${t("warehouseStock")}: ${item.name}`}
			maxWidth="min(95vw, 900px)"
		>
			{isLoading && <LoadingSpinner />}
			{!isLoading && warehouses.length === 0 && (
				<p className={styles.empty}>{t("noStockAvailable")}</p>
			)}
			{!isLoading && warehouses.length > 0 && (
				<>
					<p className={styles.totalStock}>
						{t("currentStock")}: <strong>{formatQuantity(totalStock)}</strong> ({warehouses.length}{" "}
						{t("warehouses")})
					</p>
					<table className={styles.stockTable}>
						<thead>
							<tr>
								<th>{t("code")}</th>
								<th>{t("name")}</th>
								<th>{t("currentStock")}</th>
								<th>{t("reservedAmount")}</th>
								<th>{t("onOrderAmount")}</th>
								<th>{t("minStock")}</th>
								<th>{t("maxStock")}</th>
							</tr>
						</thead>
						<tbody>
							{warehouses.map((ws) => (
								<tr key={ws.id}>
									<td>{ws.warehouseCode || "-"}</td>
									<td>{ws.warehouseName || "-"}</td>
									<td>{formatQuantity(ws.currentStock)}</td>
									<td>{formatQuantity(ws.reservedAmount)}</td>
									<td>{formatQuantity(ws.onOrderAmount)}</td>
									<td>{formatQuantity(ws.minStock)}</td>
									<td>{formatQuantity(ws.maxStock)}</td>
								</tr>
							))}
						</tbody>
					</table>
				</>
			)}
		</Modal>
	);
}

function MovementsModal({
	item,
	onClose,
}: {
	item: { id: string; name: string };
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { data, isLoading } = useStockItemMovements(item.id);
	const movements = data?.movements ?? [];

	return (
		<Modal
			isOpen
			onClose={onClose}
			title={`${t("stockRecordList")}: ${item.name}`}
			maxWidth="min(95vw, 900px)"
		>
			{isLoading && <LoadingSpinner />}
			{!isLoading && movements.length === 0 && (
				<p className={styles.empty}>{t("noStockAvailable")}</p>
			)}
			{!isLoading && movements.length > 0 && (
				<table className={styles.stockTable}>
					<thead>
						<tr>
							<th>{t("date")}</th>
							<th>{t("document")}</th>
							<th>{t("warehouse")}</th>
							<th>{t("counterpart")}</th>
							<th>{t("quantity")}</th>
							<th>{t("price")}</th>
						</tr>
					</thead>
					<tbody>
						{movements.map((m, i) => (
							<tr key={i}>
								<td>{formatDate(m.date)}</td>
								<td>{m.document || "-"}</td>
								<td>{m.warehouseCode || "-"}</td>
								<td>{m.counterpartName || "-"}</td>
								<td>{formatQuantity(m.quantity)}</td>
								<td>
									{m.price != null
										? `${formatAmount(m.price)}${m.currency ? m.currency : ""}`
										: "-"}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</Modal>
	);
}

function SerialsModal({
	item,
	onClose,
}: {
	item: { id: string; name: string };
	onClose: () => void;
}) {
	const { t } = useTranslation();
	const { data, isLoading } = useStockItemSerials(item.id);
	const serials = data?.serials ?? [];

	return (
		<Modal
			isOpen
			onClose={onClose}
			title={`${t("serialList")}: ${item.name}`}
			maxWidth="min(95vw, 700px)"
		>
			{isLoading && <LoadingSpinner />}
			{!isLoading && serials.length === 0 && (
				<p className={styles.empty}>{t("noStockAvailable")}</p>
			)}
			{!isLoading && serials.length > 0 && (
				<table className={styles.stockTable}>
					<thead>
						<tr>
							<th>{t("warehouse")}</th>
							<th>{t("serialNo")}</th>
						</tr>
					</thead>
					<tbody>
						{serials.map((s, i) => (
							<tr key={i}>
								<td>{s.warehouseCode || "-"}</td>
								<td>{s.serialNo}</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
		</Modal>
	);
}
